import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CallToActionButton,
  Container,
  Divider,
  FlexBox,
  Panel,
  Spacer,
  StandByOverlay
} from '@directsoftware/ui-kit-web-admin';
import {
  fetchWlsLynnbrookConfig,
  fetchWlsTargetId,
  fetchWlsUnitPricing,
  fetchWlsCouponCodes,
  fetchWlsCheckoutAvailability,
  fetchWlsRoomAvailability,
  fetchWlsAuthConfig,
  reportWlsAuthConfigError,
  fetchWlsCheckoutPage
} from '../../utils/wls-api-client';
import moment from 'moment/moment';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { isNull } from 'lodash';
import ReactI18n from 'react-i18n';
import {
  availabilityErrorText,
  availabilityFromError,
  buildBookingRange,
  createRequestTracker,
  invalidDatesAvailability,
  isClientAvailabilityError,
  isDisplayableDateRangeError,
  validateDateRange
} from '../../utils/wls-booking-range';
import { checkoutAvailabilityRequest } from '../../utils/wls-checkout-availability';
import {
  selectCheckoutState,
  updateCheckoutSlice
} from '../../redux/slices/checkout';
import { selectUiState, updateUiSlice } from '../../redux/slices/ui';
import {
  checkoutVerificationRequired,
  visibleCheckoutSteps
} from '../../utils/checkout-step-state';
import { createQuoteAddonSelection } from '../../utils/checkout-quote-addons';
import CheckoutSteps from './shared/checkout-steps';
import TripDetails from './shared/trip-details';
import { updateQueryString } from '../listings/resources/shared-functions';
import ExtraAddons from './shared/extra-addons';
import ContactInfo from './shared/contact-info';
import ContactAddress from './shared/contact-address';
import StripeV2 from '../stripe/stripe-v2';
import FooterFinal from '../layout/footer-final';
import CheckoutListingInfo from './shared/checkout-listing-info';
import CheckoutPricingInfo from './shared/checkout-pricing-info';
import { useDetectMobile } from '../../../shared/hooks/useDetectMobile';
import CheckoutMobilePricing from './shared/checkout-mobile-pricing';
import CheckoutLeftSkeleton from './skeletons/checkout-left-skeleton';
import CheckoutRightSkeleton from './skeletons/checkout-right-skeleton';
import Meta from './meta';
import LynnbrookPaymentV2 from '../lynnbrook/lynnbrook-payment-v2';
import CheckoutGuestInfoCollapsedV2 from './shared/checkout-guest-info-collapsed-v2';

const CheckoutV2 = props => {
  const translate = ReactI18n.getIntlMessage;
  const { isLargeTabletOrGreater } = useDetectMobile();
  const dispatch = useDispatch();
  const brand = useSelector(state => state.brand);
  const checkoutState = useSelector(selectCheckoutState);
  const { checkoutStateChanged } = useSelector(selectUiState);
  const [isLynnbrook, setIsLynnbrook] = useState(false);
  const [authenticateEnabled, setAuthenticateEnabled] = useState(false);
  const quoteAddons = useRef(createQuoteAddonSelection()).current;

  // A response is only applied when it comes from the newest request of its
  // kind and the range it was issued for is still in state, so a slow reply
  // for an earlier range or guest count cannot overwrite a newer one.
  const currentBookingRange = useRef(checkoutState.bookingDaysInclusive);
  currentBookingRange.current = checkoutState.bookingDaysInclusive;
  const availabilityRequests = useRef(createRequestTracker()).current;
  const pricingRequests = useRef(createRequestTracker()).current;
  const trackRequest = (requests, range) => {
    const token = requests.start();
    return () =>
      requests.isLatest(token) && currentBookingRange.current === range;
  };

  const updateCheckoutState = (data, isFilterChange = false) => {
    if (data.addonFeeIds && data.checkoutCheckPricing) {
      quoteAddons.override(queryString.parse(window.location.search).quote_id);
    }
    dispatch(updateCheckoutSlice(data));
    if (isFilterChange) dispatch(updateUiSlice({ checkoutStateChanged: true }));
  };

  const parseCheckInCheckOut = () => {
    const parsedQuery = queryString.parse(location.search);
    const parsedCheckInDate = moment(
      parsedQuery['check-in'],
      'DD-MM-YYYY'
    ).format('DD-MM-YYYY');
    const parsedCheckOutDate = moment(
      parsedQuery['check-out'],
      'DD-MM-YYYY'
    ).format('DD-MM-YYYY');

    return { check_in: parsedCheckInDate, check_out: parsedCheckOutDate };
  };

  const checkPricing = () => {
    const requestedRange = checkoutState.bookingDaysInclusive;
    const isLatestRequest = trackRequest(pricingRequests, requestedRange);
    const parsedQuery = queryString.parse(location.search);
    const { guests, deliveryLocation, customLocation } = parsedQuery;
    const delivery = customLocation
      ? sessionStorage.customLocation
      : deliveryLocation;

    const applyPricing = response => {
      if (!isLatestRequest()) return;
      const data = response.data;
      updateCheckoutState({
        ...quoteAddons.seed(parsedQuery.quote_id, data),
        fees: data.fees,
        pricing: data,
        availabilityLoading: false,
        checkoutTotal: data.total
      });
    };

    fetchWlsUnitPricing(brand, checkoutState.listing.id, {
      quote_id: checkoutState.quoteId,
      addon_fee_ids: quoteAddons.pricingIds(
        parsedQuery.quote_id,
        checkoutState.addonFeeIds
      ),
      coupon_code: checkoutState.couponCode,
      booking_range: JSON.stringify(checkoutState.bookingDaysInclusive),
      num_guests: guests,
      delivery_location: delivery
    })
      .then(applyPricing)
      .catch(error => { console.warn(error); });
  };

  const addCouponCode = code => {
    updateCheckoutState({ couponCode: code, checkoutCheckPricing: true }, true);
  };

  const verifyCouponCode = () => {
    if (isNull(checkoutState.allCouponCodes)) {
      updateCheckoutState({ badCode: true });
    } else if (
      checkoutState.allCouponCodes &&
      checkoutState.allCouponCodes.includes(checkoutState.couponCode)
    ) {
      addCouponCode(checkoutState.couponCode);
    } else {
      updateCheckoutState({ badCode: true });
    }
  };

  const fetchCouponCodes = () => {
    const applyCoupons = data => {
      updateCheckoutState({ allCouponCodes: data });
    };

    fetchWlsCouponCodes(brand, checkoutState.listing.id)
      .then(res => {
        const data = res.data?.data ? res.data.data : res.data;
        applyCoupons(data);
      })
      .catch(error => { console.warn(error); });
  };

  const onAvailabilityLoaded = data => {
    updateCheckoutState({ availability: data });
    if (data.bookable) {
      fetchCouponCodes();
      verifyCouponCode();
      checkPricing();
    }
  };

  const checkAvailability = () => {
    const requestedRange = checkoutState.bookingDaysInclusive;
    const isLatestRequest = trackRequest(availabilityRequests, requestedRange);
    const { roomType, params } = checkoutAvailabilityRequest({
      listing: checkoutState.listing,
      unit: checkoutState.unit,
      bookingRange: checkoutState.bookingDaysInclusive,
      guests: checkoutState.guests,
      quoteId: checkoutState.quoteId
    });
    const request = roomType
      ? fetchWlsRoomAvailability
      : fetchWlsCheckoutAvailability;
    request(brand, checkoutState.listing.id, params)
      .then(response => {
        if (!isLatestRequest()) return;
        onAvailabilityLoaded(response.data);
      })
      .catch(error => {
        if (!isLatestRequest()) return;
        const availability = availabilityFromError(error);
        if (availability) {
          updateCheckoutState({ availability, availabilityLoading: false });
        }
        console.warn(error);
      });
  };

  const parseUrl = () => {
    const parsedQuery = queryString.parse(location.search);

    if (parsedQuery['check-in'] && parsedQuery['check-out']) {
      const parsedCheckInDate = moment(parsedQuery['check-in'], 'DD-MM-YYYY');
      const parsedCheckOutDate = moment(parsedQuery['check-out'], 'DD-MM-YYYY');
      let guests = parsedQuery.guests;

      if (isNaN(guests) || guests < 1) {
        guests = 1;
      } else if (guests > checkoutState.unit.num_sleep) {
        guests = checkoutState.unit.num_sleep;
      }

      const bookingRangeError = validateDateRange(
        parsedCheckInDate,
        parsedCheckOutDate
      );
      const bookingDaysInclusive = buildBookingRange(
        parsedCheckInDate,
        parsedCheckOutDate
      );

      const quoteId = parsedQuery.quote_id;
      updateCheckoutState(
        {
          checkInDate: moment(parsedCheckInDate),
          checkInDateRaw: parsedQuery['check-in'],
          checkOutDate: moment(parsedCheckOutDate),
          bookingDaysInclusive,
          bookingLength: bookingDaysInclusive
            ? bookingDaysInclusive.length - 1
            : 0,
          guests,
          datesParsed: true,
          addonFeeIds: checkoutState.addonFeeIds,
          quoteId: quoteId || checkoutState.quoteIdFromDb,
          ...(bookingRangeError
            ? {
                availability: invalidDatesAvailability(bookingRangeError),
                availabilityLoading: false,
                isAvailable: false
              }
            : { checkoutCheckAvailability: true })
        },
        true
      );
    } else {
      updateCheckoutState({
        datesParsed: false,
        isAvailable: false
      });
    }
  };

  // Direct 2.0 prices the stay while building the checkout page, so an
  // invalid range is rejected before parseUrl would ever run. Show the
  // date error directly instead of requesting the page with those dates.
  const showInvalidUrlDates = bookingRangeError => {
    const parsedQuery = queryString.parse(location.search);

    updateCheckoutState({
      checkInDate: moment(parsedQuery['check-in'], 'DD-MM-YYYY'),
      checkInDateRaw: parsedQuery['check-in'],
      checkOutDate: moment(parsedQuery['check-out'], 'DD-MM-YYYY'),
      bookingDaysInclusive: null,
      bookingLength: 0,
      datesParsed: true,
      isAvailable: false,
      availability: invalidDatesAvailability(bookingRangeError),
      availabilityLoading: false,
      pricing: null,
      loading: false
    });
  };

  const fetchBookingInfo = () => {
    const parsedQuery = queryString.parse(location.search);
    const {
      couponCode,
      guests,
      customLocation,
      deliveryLocation
    } = parsedQuery;
    const parsedDates = parseCheckInCheckOut();
    const bookingRangeError =
      parsedQuery['check-in'] && parsedQuery['check-out']
        ? validateDateRange(
            moment(parsedQuery['check-in'], 'DD-MM-YYYY'),
            moment(parsedQuery['check-out'], 'DD-MM-YYYY')
          )
        : null;

    if (isDisplayableDateRangeError(bookingRangeError)) {
      showInvalidUrlDates(bookingRangeError);
      return;
    }

    updateCheckoutState({ couponCode });
    fetchWlsCheckoutPage(brand, props.match.params.id, {
      check_in: parsedDates.check_in,
      check_out: parsedDates.check_out,
      num_guests: guests,
      coupon_code: couponCode,
      addon_fee_ids: parsedQuery.addonFeeIds,
      quote_id: parsedQuery.quote_id
    })
      .then(response => {
        const data = response.data;
        dispatch(
          updateUiSlice({
            checkoutVisibleSteps: visibleCheckoutSteps(
              checkoutVerificationRequired(data)
            )
          })
        );
        updateCheckoutState(
          {
            brandCurrency: data.brand_currency,
            ...quoteAddons.seed(parsedQuery.quote_id, data),
            checkoutTotal: data.checkout_total,
            contractTermsAndConditions: data.contract_terms_and_conditions,
            deposits: data.deposits,
            delivery_locations: data.delivery_locations,
            deliveryLocation: customLocation
              ? sessionStorage.customLocation
              : deliveryLocation,
            featuredImage: data.featured_image,
            fees: data.fees,
            listing: data.listing,
            location: data.location,
            obfuscatedAddress: data.obfuscated_address,
            property: data.property,
            quoteIdFromDb: data.quote_id,
            rentalAgreement: data.rental_agreement,
            requiredAge: data.required_age,
            slug: data.slug,
            stripePublishableKey: data.stripe_publishable_key,
            stripeAccountId: data.stripe_account_id,
            stripeCustomerId: data.stripe_customer_id,
            stripeIntentId: data.stripe_intent_id,
            unit: data.unit,
            verifyAddress: data.verify_address,
            verifyAge: data.verify_age,
            verifyImage: data.verify_image,
            verifyImageDescription: data.verify_image_description,
            verifySignature: data.verify_signature,
            loading: false,
            checkoutParseUrl: true,
            checkoutGetLynnbrookTargetId: true,
            accountPersonCreated: false,
            lynnbrookErrors: [],
            fetchingLynnbrookConfig: false,
            lynnbrookConfig: {
              accountTargetId: null,
              accountPersonId: null,
              sessionId: null,
              token: null
            },
            lynnbrookCardAccepted: false
          },
          true
        );
      })
      .catch(error => {
        const availability = availabilityFromError(error, {
          showServerMessage: true
        });
        if (availability) {
          updateCheckoutState({
            availability,
            availabilityLoading: false,
            isAvailable: false,
            loading: false
          });
        }
        console.warn(error);
      });
  };

  const fetchLynbrookConfig = targetId => {
    const name = `${checkoutState.customerFirstName} ${
      checkoutState.customerLastName
    }`;
    const applyConfig = res => {
      const data = res.data;
      updateCheckoutState({
        fetchingLynnbrookConfig: false,
        accountPersonCreated: true,
        lynnbrookConfig: {
          ...checkoutState.lynnbrookConfig,
          accountTargetId:
            data.target_id || checkoutState.lynnbrookConfig.accountTargetId,
          accountPersonId: data.account_person_id,
          sessionId: data.session_id,
          token: process.env.APTEXX_API_TOKEN
        }
      });
    };

    fetchWlsLynnbrookConfig(brand, checkoutState.listing.id, {
      customer_email: checkoutState.customerEmail,
      customer_name: name,
      customer_telephone: checkoutState.customerTelephone,
      customer_date_of_birth: checkoutState.customerDateOfBirth,
      target_id: targetId
    })
      .then(applyConfig)
      .catch(error => { console.warn(error); });
  };

  const fetchLynnbrookTargetId = () => {
    const applyTargetId = res => {
      updateCheckoutState({
        lynnbrookConfig: {
          ...checkoutState.lynnbrookConfig,
          accountTargetId: res.data.target_id
        }
      });
    };

    fetchWlsTargetId(brand, checkoutState.listing.id)
      .then(applyTargetId)
      .catch(error => { console.warn(error); });
  };

  const checkFieldIsInvalid = field => {
    return field === '' || field === undefined || !field;
  };

  useEffect(() => {
    fetchWlsAuthConfig(brand)
      .then(res => {
        if (res.data.authenticate_enabled && res.data.medallion_workflow_id) {
          setAuthenticateEnabled(true);
        }
      })
      .catch(reportWlsAuthConfigError);
  }, []);

  const checkAuthenticate = () => authenticateEnabled;

  const createAccountPerson = () => {
    const {
      customerFirstName,
      customerLastName,
      customerEmail,
      customerTelephone,
      customerDateOfBirth,
      adrStreet,
      adrCity,
      adrState,
      adrPostalCode,
      adrCountry
    } = checkoutState;

    const errorArray = [];

    if (checkFieldIsInvalid(customerFirstName))
      errorArray.push('customerFirstName');
    if (checkFieldIsInvalid(customerLastName))
      errorArray.push('customerLastName');
    if (
      checkFieldIsInvalid(customerEmail) ||
      checkoutState.validationErrors.length > 0
    )
      errorArray.push('customerEmail');
    if (checkFieldIsInvalid(customerTelephone))
      errorArray.push('customerTelephone');
    if (checkAuthenticate() && checkFieldIsInvalid(customerDateOfBirth))
      errorArray.push('customerDateOfBirth');
    if (checkFieldIsInvalid(adrStreet)) errorArray.push('adrStreet');
    if (checkFieldIsInvalid(adrCity)) errorArray.push('adrCity');
    if (checkFieldIsInvalid(adrState)) errorArray.push('adrState');
    if (checkFieldIsInvalid(adrPostalCode)) errorArray.push('adrPostalCode');
    if (checkFieldIsInvalid(adrCountry)) errorArray.push('adrCountry');

    updateCheckoutState({ lynnbrookErrors: errorArray });

    if (errorArray.length === 0) {
      updateCheckoutState({ fetchingLynnbrookConfig: true });
      fetchLynbrookConfig(checkoutState.lynnbrookConfig.accountTargetId);
    }
  };

  useEffect(
    () => {
      if (checkoutStateChanged) {
        dispatch(updateUiSlice({ checkoutStateChanged: false }));
        if (checkoutState.checkoutGetLynnbrookTargetId && isLynnbrook) {
          updateCheckoutState({ checkoutGetLynnbrookTargetId: false });
          fetchLynnbrookTargetId();
        }
        if (checkoutState.updateUrl) {
          updateCheckoutState(
            { updateUrl: false, checkoutParseUrl: true },
            true
          );
          updateQueryString(checkoutState);
        }
        if (checkoutState.checkoutParseUrl) {
          updateCheckoutState({ checkoutParseUrl: false });
          parseUrl();
        }
        if (
          checkoutState.checkoutCheckAvailability &&
          checkoutState.bookingDaysInclusive
        ) {
          updateCheckoutState({ checkoutCheckAvailability: false });
          checkAvailability();
        }
        if (checkoutState.checkoutCheckPricing) {
          updateCheckoutState({ checkoutCheckPricing: false });
          checkPricing();
        }
      }
    },
    [checkoutState, checkoutStateChanged]
  );

  useEffect(() => {
    fetchBookingInfo();
    dispatch(updateUiSlice({ checkoutActiveStep: 'stepOne' }));
    setIsLynnbrook(brand.organization.payment_processor === 2);
  }, []);

  const returnToListing = () => {
    const { slug } = props.match.params;
    if (slug) {
      props.history.push(`/listings/${slug}`);
    } else if (props.history.length > 1) {
      props.history.goBack();
    } else {
      props.history.push('/listings/search');
    }
  };

  const leftColumnFallback =
    checkoutState.availability && !checkoutState.availability.bookable ? (
      <Panel addShadow>
        <Box padding="l">
          {availabilityErrorText(checkoutState.availability, translate)}
          {isClientAvailabilityError(checkoutState.availability) && (
            <>
              <Spacer size="m" />
              <CallToActionButton
                onClick={returnToListing}
                customButtonColor={brand.brand_info.colors?.color_primary}
                customTextColor={brand.brand_info.colors?.color_primary_text}
              >
                {translate('global.actions.back.with_arrow')}
              </CallToActionButton>
            </>
          )}
        </Box>
      </Panel>
    ) : (
      <CheckoutLeftSkeleton />
    );

  return !checkoutState.loading ? (
    <>
      <Meta />
      <Container variation="extended-wls">
        <CheckoutSteps />
        <FlexBox className="detailsLayout checkoutLayout">
          <Box flex="1">
            {checkoutState.availability &&
            checkoutState.availability.bookable &&
            checkoutState.pricing ? (
              <>
                <TripDetails updateCheckoutState={updateCheckoutState} />
                <ExtraAddons updateCheckoutState={updateCheckoutState} />
                {!isLargeTabletOrGreater && (
                  <CheckoutMobilePricing
                    updateCheckoutState={updateCheckoutState}
                  />
                )}
                {isLynnbrook ? (
                  <>
                    {!checkoutState.accountPersonCreated ? (
                      <>
                        <ContactInfo
                          updateCheckoutState={updateCheckoutState}
                          isLynnbrook
                          isAuthenticate={checkAuthenticate()}
                        />
                        <ContactAddress
                          stateObject={checkoutState}
                          updateCheckoutState={updateCheckoutState}
                        />
                        <CallToActionButton
                          onClick={() => {
                            createAccountPerson();
                          }}
                          size="large"
                          isFullWidth
                          isDisabled={checkoutState.fetchingLynnbrookConfig}
                          customButtonColor={
                            brand.brand_info.colors?.color_primary
                          }
                          customTextColor={
                            brand.brand_info.colors?.color_primary_text
                          }
                        >
                          Continue
                        </CallToActionButton>
                        <Spacer size="l" />
                        <Divider />
                        <Spacer size="l" />
                      </>
                    ) : (
                      <CheckoutGuestInfoCollapsedV2
                        editOnClick={() =>
                          updateCheckoutState({ accountPersonCreated: false })
                        }
                      />
                    )}
                  </>
                ) : (
                  <>
                    <ContactInfo updateCheckoutState={updateCheckoutState} />
                    <ContactAddress
                      stateObject={checkoutState}
                      updateCheckoutState={updateCheckoutState}
                    />
                  </>
                )}
                {isLynnbrook ? (
                  <LynnbrookPaymentV2
                    useState={updateCheckoutState}
                    fetchLynbrookConfig={fetchLynbrookConfig}
                    addonFeeIds={checkoutState.addonFeeIds}
                    availability={checkoutState.availability}
                    booking={props.booking}
                    bookingDaysInclusive={checkoutState.bookingDaysInclusive}
                    brand_info={brand.brand_info}
                    checkInDate={checkoutState.checkInDate}
                    checkOutDate={checkoutState.checkOutDate}
                    couponCode={checkoutState.couponCode}
                    customerEmail={checkoutState.customerEmail}
                    customerName={`${checkoutState.customerFirstName} ${
                      checkoutState.customerLastName
                    }`}
                    customerPostalCode={checkoutState.adrPostalCode}
                    customerTelephone={checkoutState.customerTelephone}
                    customerDateOfBirth={checkoutState.customerDateOfBirth}
                    isAuthenticate={checkAuthenticate()}
                    guests={checkoutState.guests}
                    listing={checkoutState.listing}
                    max_guests={checkoutState.unit.num_sleep}
                    pricing={checkoutState.pricing}
                    quoteId={checkoutState.quoteId}
                    rental_agreement={checkoutState.rentalAgreement}
                    slug={checkoutState.slug}
                    stripeCustomerId={checkoutState.stripeCustomerId}
                    stripeIntentId={checkoutState.stripeIntentId}
                    stripePublishableKey={checkoutState.stripePublishableKey}
                    unit={checkoutState.unit}
                    updateGuests={guests => {
                      updateCheckoutState(
                        {
                          availability: null,
                          bookingType: null,
                          pricing: null,
                          guests,
                          updateUrl: true
                        },
                        true
                      );
                    }}
                    verifyImage={checkoutState.verifyImage}
                    verifySignature={checkoutState.verifySignature}
                    verifyAge={checkoutState.verifyAge}
                    verifyAddress={checkoutState.verifyAddress}
                    updateCheckoutState={updateCheckoutState}
                    urlLocation={props.location}
                    validationErrors={checkoutState.validationErrors}
                  />
                ) : (
                  <StripeV2
                    addonFeeIds={checkoutState.addonFeeIds}
                    availability={checkoutState.availability}
                    booking={props.booking}
                    bookingDaysInclusive={checkoutState.bookingDaysInclusive}
                    brand_info={brand.brand_info}
                    brand={brand}
                    checkInDate={checkoutState.checkInDate}
                    checkOutDate={checkoutState.checkOutDate}
                    couponCode={checkoutState.couponCode}
                    customerEmail={checkoutState.customerEmail}
                    customerName={checkoutState.customerName}
                    customerPostalCode={checkoutState.adrPostalCode}
                    customerTelephone={checkoutState.customerTelephone}
                    deliveryLocation={checkoutState.deliveryLocation}
                    adrStreet={checkoutState.adrStreet}
                    adrUnit={checkoutState.adrUnit}
                    adrCity={checkoutState.adrCity}
                    adrState={checkoutState.adrState}
                    adrCountry={checkoutState.adrCountry}
                    adrPostalCode={checkoutState.adrPostalCode}
                    adrCountryShort={checkoutState.adrCountryShort}
                    guests={checkoutState.guests}
                    listing={checkoutState.listing}
                    max_guests={checkoutState.unit.num_sleep}
                    pricing={checkoutState.pricing}
                    quoteId={checkoutState.quoteId}
                    rental_agreement={checkoutState.rentalAgreement}
                    slug={checkoutState.slug}
                    stripeCustomerId={checkoutState.stripeCustomerId}
                    stripeIntentId={checkoutState.stripeIntentId}
                    stripePublishableKey={checkoutState.stripePublishableKey}
                    stripeAccountID={checkoutState.stripeAccountId}
                    unit={checkoutState.unit}
                    updateGuests={guests => {
                      updateCheckoutState(
                        {
                          availability: null,
                          bookingType: null,
                          pricing: null,
                          guests,
                          updateUrl: true
                        },
                        true
                      );
                    }}
                    verifyImage={checkoutState.verifyImage}
                    verifySignature={checkoutState.verifySignature}
                    verifyAge={checkoutState.verifyAge}
                    verifyAddress={checkoutState.verifyAddress}
                    urlLocation={props.location}
                    validationErrors={checkoutState.validationErrors}
                  />
                )}
              </>
            ) : (
              leftColumnFallback
            )}
          </Box>
          {isLargeTabletOrGreater && (
            <Box className="detailsLayout__right checkoutLayout">
              {!checkoutState.availabilityLoading ? (
                <Panel addShadow>
                  {checkoutState.availability?.bookable &&
                  checkoutState.pricing ? (
                    <>
                      <CheckoutListingInfo />
                      <CheckoutPricingInfo
                        updateCheckoutState={updateCheckoutState}
                      />
                    </>
                  ) : (
                    <Box padding="l">
                      {availabilityErrorText(
                        checkoutState.availability,
                        translate
                      )}
                    </Box>
                  )}
                </Panel>
              ) : (
                <CheckoutRightSkeleton />
              )}
            </Box>
          )}
        </FlexBox>
      </Container>
      <Spacer size="xxl" />
      <FooterFinal />
    </>
  ) : (
    <StandByOverlay freezeBody={false} reveal={checkoutState.loading} />
  );
};

export default CheckoutV2;
